import type { MouseEventHandler, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowUpRight, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

type PillVariant = 'light' | 'glass' | 'dark'
type PillSize = 'sm' | 'md' | 'lg'

interface PillButtonProps {
    children: ReactNode
    href?: string
    variant?: PillVariant
    size?: PillSize
    type?: 'button' | 'submit'
    disabled?: boolean
    loading?: boolean
    showIcon?: boolean
    className?: string
    ariaLabel?: string
    onClick?: MouseEventHandler<HTMLElement>
}

const variants: Record<PillVariant, { base: string, icon: string }> = {
    light: {
        base: 'bg-cream text-black hover:bg-white shadow-[0_18px_40px_-18px_rgba(226,243,122,0.45)]',
        icon: 'bg-black text-cream'
    },
    glass: {
        base: 'glass text-cream hover:bg-white/10',
        icon: 'bg-cream text-black'
    },
    dark: {
        base: 'bg-black text-cream ring-1 ring-white/[0.12] hover:ring-white/25',
        icon: 'bg-cream text-black'
    }
}

const sizes: Record<PillSize, { base: string, icon: string, glyph: string }> = {
    sm: { base: 'h-10 gap-2.5 pl-4 pr-1.5 text-[13px]', icon: 'size-7', glyph: 'size-3.5' },
    md: { base: 'h-12 gap-3 pl-5 pr-2 text-sm', icon: 'size-8', glyph: 'size-4' },
    lg: { base: 'h-14 gap-3.5 pl-6 pr-2 text-[15px]', icon: 'size-10', glyph: 'size-4' }
}

/** White pill with black text and a small dark circular arrow on the right. */
export const PillButton = ({
    children,
    href,
    variant = 'light',
    size = 'md',
    type = 'button',
    disabled = false,
    loading = false,
    showIcon = true,
    className,
    ariaLabel,
    onClick
}: PillButtonProps) => {
    const classes = cn(
        'group inline-flex shrink-0 items-center justify-between whitespace-nowrap rounded-full font-medium tracking-[-0.01em] transition-all duration-500 ease-cinema',
        'disabled:pointer-events-none disabled:opacity-50',
        variants[variant].base,
        sizes[size].base,
        !showIcon && 'pr-5',
        className
    )

    const content = (
        <>
            <span>
                {children}
            </span>

            {showIcon && (
                <span
                    aria-hidden='true'
                    className={cn(
                        'grid place-items-center rounded-full transition-transform duration-500 ease-cinema',
                        !loading && 'group-hover:rotate-45',
                        variants[variant].icon,
                        sizes[size].icon
                    )}
                >
                    {loading
                        ? <Loader2 className={cn('animate-spin', sizes[size].glyph)} />
                        : <ArrowUpRight className={sizes[size].glyph} strokeWidth={2.2} />}
                </span>
            )}
        </>
    )

    if (href && !disabled) {
        return (
            <Link href={href} className={classes} aria-label={ariaLabel} onClick={onClick}>
                {content}
            </Link>
        )
    }

    return (
        <button
            type={type}
            className={classes}
            disabled={disabled || loading}
            aria-label={ariaLabel}
            aria-busy={loading || undefined}
            onClick={onClick}
        >
            {content}
        </button>
    )
}
